import { decodeToken } from "@/api/tokenManager";
import { Report, VolunteerActivism, NearMe } from "@mui/icons-material";
import { Box, Button } from "@mui/material";
import { useState } from "react";
import { toast } from "react-toastify";
import UpdatePopup from "../contribute/ContributeModal";
import { useNavigate } from "react-router-dom";
import ReportIssueModal from "./ReportIssueModal";
import { Place } from "@/types/common";

type ActionButtonGroupProps = {
  place: Place;
  onClose?: () => void;
};

type ButtonConfig = {
  label: string;
  onClick: (e: React.MouseEvent) => void;
  disabled?: boolean;
  loading?: boolean;
  icon: React.ReactNode;
  activeColor?: string;
  disabledColor?: string;
  activeBg?: string;
  disabledBg?: string;
  hoverBg?: string;
};

const ActionButtonGroup = ({ place, onClose }: ActionButtonGroupProps) => {
  const navigate = useNavigate();
  const { user_id } = decodeToken() ?? {};
  const [showUpdatePopup, setShowUpdatePopup] = useState(false);
  const [openReportModal, setOpenReportModal] = useState(false);

  const {
    place_id,
    name,
    contributions,
    has_pending_contribution,
    status,
    g_place_id,
    address_text,
    coordinates,
    country_code,
    city,
    postcode,
    subcategory_id,
  } = place || {};
  const { submitted_by } = contributions?.[0] ?? {};

  const hasContributions = contributions && contributions.length > 0;

  const placeState = {
    place_id,
    g_place_id,
    name,
    address_text,
    coordinates,
    country_code,
    city,
    postcode,
    subcategory_id,
  };

  const handleContribute = (e: React.MouseEvent) => {
    e.stopPropagation();

    const cantContribute =
      submitted_by?.user_id === user_id && status === "pending";

    if (cantContribute) {
      toast.error(
        "Your contribution is pending approval. You cannot contribute at this time.",
      );
      return;
    }

    if (hasContributions) {
      setShowUpdatePopup(true);
    } else {
      localStorage.setItem("from_explore_to_contribute", "true");
      navigate("/contribution/create", { state: placeState });
      onClose?.();
    }
  };

  const handleNavigate = async (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/navigation/${place_id}`, {
      state: {
        place_name: name,
      },
    });
    onClose?.();
  };

  const handleProceedFromPopup = (update_reason: string) => {
    setShowUpdatePopup(false);
    localStorage.setItem("from_explore_to_contribute", "true");
    localStorage.setItem("update_reason", update_reason);
    navigate("/contribution/create", { state: placeState });
  };

  const buttons: ButtonConfig[] = [
    {
      label: "Navigate",
      onClick: handleNavigate,
      disabled: !hasContributions,
      loading: false,
      icon: <NearMe fontSize="small" />,
      activeColor: "#ffff",
      disabledColor: "#ffff",
      activeBg: "#9443DF",
      disabledBg: "#ccc",
      hoverBg: "#ede9fe",
    },
    {
      label: "Contribute",
      onClick: handleContribute,
      disabled: has_pending_contribution,
      loading: false,
      icon: <VolunteerActivism fontSize="small" />,
    },
    {
      label: "Report",
      onClick: (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!hasContributions) return;
        setOpenReportModal(true);
      },
      disabled: !hasContributions || has_pending_contribution,
      loading: false,
      icon: <Report fontSize="small" />,
    },
  ];

  return (
    <>
      <Box sx={{ display: "flex", gap: "4%" }}>
        {buttons.map((button, index) => {
          const isDisabled = !!button.disabled;
          const activeColor = button.activeColor ?? "#9443DF";
          const disabledColor = button.disabledColor ?? "#C4B5FD";
          const activeBg = button.activeBg ?? "#F0DEFF";
          const disabledBg = button.disabledBg ?? "#F5F3FF";
          const hoverBg = button.hoverBg ?? "#ede9fe";
          const startIconColor = isDisabled ? disabledColor : activeColor;

          return (
            <Button
              key={index}
              variant="contained"
              fullWidth
              disabled={isDisabled}
              onClick={(e) => button.onClick(e)}
              startIcon={button.icon}
              sx={{
                fontSize: { xs: "3.4vw", sm: 14 },
                fontWeight: 700,
                color: isDisabled ? disabledColor : activeColor,
                borderRadius: "20px",
                background: isDisabled ? disabledBg : activeBg,
                boxShadow: "none",
                py: "2.1%",
                "&:hover": {
                  background: isDisabled ? disabledBg : hoverBg,
                },
                "& .MuiButton-startIcon": {
                  color: startIconColor,
                  opacity: isDisabled ? 0.6 : 1,
                  "& svg": {
                    color: startIconColor,
                  },
                },
                "&.Mui-disabled": {
                  background: disabledBg,
                  color: disabledColor,
                  cursor: "not-allowed",
                  "& .MuiButton-startIcon": {
                    color: disabledColor,
                    opacity: 0.6,
                    "& svg": {
                      color: disabledColor,
                    },
                  },
                },
              }}
            >
              {button.label}
            </Button>
          );
        })}
      </Box>
      <UpdatePopup
        open={showUpdatePopup}
        onClose={() => setShowUpdatePopup(false)}
        onProceed={(comment) => handleProceedFromPopup(comment)}
        placeName={name || ""}
      />
      <ReportIssueModal
        open={openReportModal}
        contributionId={contributions?.[0]?.contribution_id || ""}
        onClose={() => setOpenReportModal(false)}
      />
    </>
  );
};

export default ActionButtonGroup;
